import React, { useEffect, useReducer, useRef, useState } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  Save,
  ExternalLink,
  CheckCircle2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@cms/ui/components/card";
import { Button } from "@cms/ui/components/button";
import { Badge } from "@cms/ui/components/badge";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";
import { Alert, AlertTitle, AlertDescription } from "@cms/ui/components/alert";
import { paletteFromTokens, parseDesignTokens } from "@cms/portal-lib/palette";
import { LocalTabsHeader } from "@cms/ui/components/section-tabs";
import { SearchListingCard } from "../components/search-listing-card";
import { FaqsCard, type Faq } from "../components/faqs-card";
import { SectionCanvas, type ColumnAlignPatch } from "../components/section-canvas";
import { SectionPropertiesPanel } from "../components/section-properties-panel";
import { ColumnEditorDialog } from "../components/column-editor-dialog";
import { SectionTypeDialog } from "../components/section-type-dialog";
import { TimelineEditorDialog } from "../components/timeline-editor-dialog";
import {
  DEFAULT_VIEWPORT_WIDTH,
  ViewAsControl,
  ViewportFrame,
} from "../components/viewport-preview";
import {
  cloneSection,
  contentToSections,
  fitColumns,
  newSection,
  newTimelineSection,
  sectionsToContent,
  type PageSection,
  type SectionSettings,
  type SectionTimeline,
  type SectionType,
} from "../lib/editor-content";
import type { ClientOutletContext } from "../lib/outlet-context";

const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

interface PageRow {
  slug: string;
  title: string;
  content_md?: string | null;
  meta_title?: string | null;
  meta_desc?: string | null;
  published?: number;
  faqs?: string | Faq[] | null;
}

function parseFaqsField(value: PageRow["faqs"]): Faq[] {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function slugProblem(slug: string): string {
  if (!SLUG_PATTERN.test(slug) || slug.length > 50) {
    return "Use lowercase letters, numbers and hyphens (start with a letter or number).";
  }
  if (slug === "home") return '"home" is reserved.';
  return "";
}

type EditorTab = "general" | "content";

/** Everything the editor can change about a page — one draft, one dirty flag. */
interface PageDraft {
  title: string;
  slug: string;
  sections: PageSection[];
  metaTitle: string;
  metaDesc: string;
  faqs: Faq[];
  published: number;
}

interface DraftState {
  /** null until the page row has loaded. */
  draft: PageDraft | null;
  dirty: boolean;
}

type DraftAction =
  | { type: "load"; page: PageRow }
  | { type: "patch"; patch: Partial<PageDraft> }
  | { type: "sections"; update: (sections: PageSection[]) => PageSection[] }
  | { type: "saved" };

// Any edit ("patch"/"sections") marks the draft dirty; "load"/"saved" clear it.
function draftReducer(state: DraftState, action: DraftAction): DraftState {
  switch (action.type) {
    case "load":
      return {
        draft: {
          title: action.page.title ?? "",
          slug: action.page.slug ?? "",
          sections: contentToSections(action.page.content_md),
          metaTitle: action.page.meta_title ?? "",
          metaDesc: action.page.meta_desc ?? "",
          faqs: parseFaqsField(action.page.faqs),
          published: action.page.published ? 1 : 0,
        },
        dirty: false,
      };
    case "patch":
      return state.draft ? { draft: { ...state.draft, ...action.patch }, dirty: true } : state;
    case "sections":
      return state.draft
        ? { draft: { ...state.draft, sections: action.update(state.draft.sections) }, dirty: true }
        : state;
    case "saved":
      return { ...state, dirty: false };
  }
}

export function PageEditorPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { api, status, refreshStatus, setBreadcrumbs } =
    useOutletContext<ClientOutletContext>();

  const [page, setPage] = useState<PageRow | null>(null);
  const [loadError, setLoadError] = useState("");
  const [tab, setTab] = useState<EditorTab>("general");
  const [viewportWidth, setViewportWidth] = useState(DEFAULT_VIEWPORT_WIDTH);
  const [autoScale, setAutoScale] = useState(true);
  const [{ draft, dirty }, dispatch] = useReducer(draftReducer, { draft: null, dirty: false });
  const [editing, setEditing] = useState<{ id: string; column: number } | null>(null);
  const [pickingType, setPickingType] = useState(false);
  const [propertiesId, setPropertiesId] = useState<string | null>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [designTokens, setDesignTokens] = useState<string | null>(null);
  const [saving, setSaving] = useState("");
  const [notice, setNotice] = useState<{ variant: "success" | "destructive"; text: string } | null>(null);

  const patch = (fields: Partial<PageDraft>) => dispatch({ type: "patch", patch: fields });
  const updateSections = (update: (sections: PageSection[]) => PageSection[]) =>
    dispatch({ type: "sections", update });

  const isHome = slug === "home";
  const canPreviewUrl = status?.plan === "growth" || status?.plan === "pro";
  // FAQ schema (AEO) is a Growth/Pro feature.
  const canEditFaqs = canPreviewUrl;
  const slugError = isHome || !draft ? "" : slugProblem(draft.slug);

  useEffect(() => {
    if (!api) return;
    api.request<{ page: PageRow }>(`/tenant/pages/${slug}`)
      .then((data) => {
        setPage(data.page);
        dispatch({ type: "load", page: data.page });
      })
      .catch((err: Error) => setLoadError(err.message));
  }, [api, slug]);

  // The Content tab canvas is styled with the client's design tokens so it
  // matches the published site. Best-effort: default tokens without it.
  useEffect(() => {
    if (!api) return;
    api.request<{ config?: { design_tokens?: string | null } | null }>("/tenant/config")
      .then((data) => {
        setDesignTokens(data.config?.design_tokens ?? null);
      })
      .catch(() => {});
  }, [api]);

  // Warn before the browser unloads with unsaved changes.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Standard header breadcrumb: Pages › <this page>. The Pages crumb confirms
  // before navigating away with unsaved changes (deps include `dirty` so the
  // guard closure stays current).
  useEffect(() => {
    if (!setBreadcrumbs) return undefined;
    setBreadcrumbs(page ? [
      {
        label: "Pages",
        href: "/pages",
        onClick: (event: React.MouseEvent) => {
          if (dirty && !window.confirm("You have unsaved changes. Leave anyway?")) {
            event.preventDefault();
          }
        },
      },
      { label: page.title },
    ] : null);
    return () => setBreadcrumbs(null);
  }, [setBreadcrumbs, page, dirty]);

  // Leaving the Content tab dismisses the section properties sidebar.
  useEffect(() => {
    if (tab !== "content") setPropertiesId(null);
  }, [tab]);

  // --- Section operations (Content tab) ---

  const sections = draft?.sections ?? [];
  const editingSection = editing ? sections.find((s) => s.id === editing.id) ?? null : null;
  const propertiesSection = propertiesId
    ? sections.find((s) => s.id === propertiesId) ?? null
    : null;
  // Tenant palette (design tokens) → colour-picker presets on the canvas.
  const palettePresets = paletteFromTokens(parseDesignTokens(designTokens)).map((value) => ({ value }));

  /** "Add section" opens the type picker; the type is fixed once chosen. */
  const addSection = (type: SectionType) => {
    setPickingType(false);
    const section = type === "timeline" ? newTimelineSection() : newSection();
    updateSections((prev) => [...prev, section]);
    setEditing({ id: section.id, column: 0 });
  };

  /** Replace one column's blocks after the content modal's Done. */
  const saveColumn = (id: string, column: number, blocks: Array<Record<string, any>>) => {
    updateSections((prev) =>
      prev.map((s) =>
        s.id === id
          ? { ...s, columns: s.columns.map((col, i) => (i === column ? { ...col, blocks } : col)) }
          : s
      )
    );
    setEditing(null);
  };

  /** Replace a timeline section's content after the timeline modal's Done. */
  const saveTimeline = (id: string, timeline: SectionTimeline) => {
    updateSections((prev) => prev.map((s) => (s.id === id ? { ...s, timeline } : s)));
    setEditing(null);
  };

  /** Set one column's alignment; an undefined value clears that axis. */
  const alignColumn = (id: string, column: number, alignPatch: ColumnAlignPatch) => {
    updateSections((prev) =>
      prev.map((s) =>
        s.id === id
          ? {
              ...s,
              columns: s.columns.map((col, i) => {
                if (i !== column) return col;
                const next = { ...col, ...alignPatch };
                if (!next.align) delete next.align;
                if (!next.valign) delete next.valign;
                return next;
              }),
            }
          : s
      )
    );
  };

  /** Apply the Properties overlay's settings, reconciling the column count. */
  const updateSettings = (id: string, settings: SectionSettings) => {
    updateSections((prev) =>
      prev.map((s) =>
        s.id === id ? { ...s, settings, columns: fitColumns(s.columns, settings.columns) } : s
      )
    );
  };

  const moveSection = (id: string, direction: -1 | 1) => {
    updateSections((prev) => {
      const index = prev.findIndex((s) => s.id === id);
      const target = index + direction;
      if (index === -1 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const duplicateSection = (id: string) => {
    updateSections((prev) => {
      const index = prev.findIndex((s) => s.id === id);
      if (index === -1) return prev;
      const next = [...prev];
      next.splice(index + 1, 0, cloneSection(prev[index]));
      return next;
    });
  };

  const deleteSection = (id: string) => {
    if (!window.confirm("Delete this section? Its content will be removed.")) return;
    updateSections((prev) => prev.filter((s) => s.id !== id));
  };

  // --- Save / preview ---

  const save = async () => {
    if (!api || !draft) return;
    if (!draft.title.trim()) {
      setNotice({ variant: "destructive", text: "Give the page a title before saving." });
      return;
    }
    if (slugError) {
      setNotice({ variant: "destructive", text: slugError });
      return;
    }

    setSaving("save");
    setNotice(null);
    try {
      const body: Record<string, unknown> = {
        title: draft.title.trim(),
        content_md: sectionsToContent(draft.sections),
        meta_title: draft.metaTitle || null,
        meta_desc: draft.metaDesc || null,
        published: draft.published,
        ...(canEditFaqs && { faqs: draft.faqs }),
      };
      // Only send slug when it actually changed (renames update the PK).
      const renamed = !isHome && draft.slug !== slug;
      if (renamed) body.slug = draft.slug;

      const result = await api.request<{ slug?: string }>(`/tenant/pages/${slug}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });

      dispatch({ type: "saved" });
      // Staged save bumps content_updated_at server-side — refresh the shell so the
      // "Unpublished changes" badge + Publish button appear without a manual reload.
      refreshStatus?.();
      setNotice({ variant: "success", text: "Saved. Publish the site to make changes go live." });

      // After a rename, move to the page's new URL.
      if (renamed && result.slug && result.slug !== slug) {
        navigate(`/pages/${result.slug}`, { replace: true });
      } else {
        setPage((prev) => (prev ? { ...prev, title: draft.title.trim(), published: draft.published } : prev));
      }
    } catch (err) {
      setNotice({ variant: "destructive", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving("");
    }
  };

  const openPreview = async () => {
    if (!api || !draft) return;
    // Open the tab synchronously inside the click gesture, then redirect it
    // once the preview URL comes back. A window.open() after the awaited
    // request runs outside the user gesture and browsers block it as a popup.
    const previewTab = window.open("", "_blank");
    if (previewTab) previewTab.opener = null; // sever opener (as noreferrer did)
    setSaving("preview");
    setNotice(null);
    try {
      const result = await api.request<{ preview_url: string }>("/tenant/preview", {
        method: "POST",
        body: JSON.stringify({
          slug,
          content_md: sectionsToContent(draft.sections),
          meta_title: draft.metaTitle || null,
          meta_desc: draft.metaDesc || null,
        }),
      });
      if (previewTab) {
        previewTab.location.replace(result.preview_url);
      } else {
        // Placeholder was blocked outright (popups disabled) — best-effort retry.
        window.open(result.preview_url, "_blank", "noreferrer");
      }
      setNotice({ variant: "success", text: "Preview link opened — it expires in 30 minutes." });
    } catch (err) {
      previewTab?.close();
      setNotice({ variant: "destructive", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving("");
    }
  };

  if (loadError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Couldn't load this page</AlertTitle>
        <AlertDescription>{loadError}</AlertDescription>
      </Alert>
    );
  }

  // A loaded page implies a configured API — this narrows `api` for the JSX.
  if (!api || !page || !draft) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <LocalTabsHeader<EditorTab>
        barRef={tabsRef}
        tabs={[
          { id: "general", label: "General" },
          { id: "content", label: "Content" },
        ]}
        active={tab}
        onSelect={setTab}
        center={tab === "content" ? (
          <ViewAsControl
            width={viewportWidth}
            onChange={setViewportWidth}
            autoScale={autoScale}
            onAutoScaleChange={setAutoScale}
          />
        ) : undefined}
        actions={
          <>
            {dirty && <Badge variant="warning">Unsaved changes</Badge>}
            {canPreviewUrl && (
              <Button onClick={() => { void openPreview(); }} disabled={!!saving} variant="outline" size="sm">
                {saving === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                Preview link
              </Button>
            )}
            <Button onClick={() => { void save(); }} disabled={!!saving || !!slugError} size="sm">
              {saving === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </Button>
          </>
        }
      />

      {notice && (
        <Alert variant={notice.variant === "success" ? "default" : "destructive"} className="mb-4">
          {notice.variant === "success"
            ? <CheckCircle2 className="h-4 w-4" />
            : <AlertCircle className="h-4 w-4" />}
          <AlertDescription>{notice.text}</AlertDescription>
        </Alert>
      )}

      {tab === "general" ? (
        <div className="grid gap-4 xl:grid-cols-[1fr_280px]">
          <div className="space-y-4">
            <Card>
              <CardContent className="grid gap-4 pt-6 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="page-title">Page title</Label>
                  <Input
                    id="page-title"
                    value={draft.title}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => patch({ title: e.target.value })}
                    placeholder="e.g. About Us"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="page-slug">URL slug</Label>
                  <Input
                    id="page-slug"
                    value={draft.slug}
                    disabled={isHome}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => patch({ slug: e.target.value })}
                    placeholder="about"
                    aria-invalid={!!slugError}
                  />
                  {isHome ? (
                    <p className="text-xs text-muted-foreground">The home page always lives at /.</p>
                  ) : slugError ? (
                    <p className="text-xs text-destructive">{slugError}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Lives at /{draft.slug}/</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <SearchListingCard
              metaTitle={draft.metaTitle}
              metaDesc={draft.metaDesc}
              onMetaTitleChange={(value) => patch({ metaTitle: value })}
              onMetaDescChange={(value) => patch({ metaDesc: value })}
              titleFallback={draft.title || page.title}
              previewUrl={`${status?.site_url ?? "https://www.your-site.co.uk"}/${isHome ? "" : `${draft.slug}/`}`}
            />

            {canEditFaqs && (
              <FaqsCard faqs={draft.faqs} onChange={(next) => patch({ faqs: next })} />
            )}
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Visibility</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="inline-flex w-full rounded-md border p-0.5">
                  {[
                    { value: 0, label: "Draft" },
                    { value: 1, label: "Published" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => patch({ published: option.value })}
                      className={`flex-1 rounded px-2 py-1.5 text-sm transition-colors ${
                        draft.published === option.value
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {draft.published
                    ? "This page is included on your site the next time you publish."
                    : "Drafts stay off your live site even after you publish."}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-2 pt-6 text-xs text-muted-foreground">
                <p><strong>Save</strong> stores your changes — the live site is untouched.</p>
                <p>Use <strong>Publish</strong> in the top bar to push all saved changes live.</p>
                {!canPreviewUrl && (
                  <p>Shareable preview links are available on Growth and Pro plans.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        /* Content tab: the canvas sits in a rounded frame constrained to the
           simulated viewport width; sections render full-width within it in
           the site's own styling. */
        <>
          <ViewportFrame width={viewportWidth} autoScale={autoScale} onWidthChange={setViewportWidth}>
            <SectionCanvas
              sections={sections}
              designTokens={designTokens}
              propertiesId={propertiesId}
              onEditColumn={(id, column) => setEditing({ id, column })}
              onAlignColumn={alignColumn}
              onToggleProperties={(id) =>
                setPropertiesId((prev) => (prev === id ? null : id))
              }
              onMove={moveSection}
              onDuplicate={duplicateSection}
              onDelete={deleteSection}
              onAdd={() => setPickingType(true)}
            />
          </ViewportFrame>

          {propertiesSection && (
            <SectionPropertiesPanel
              settings={propertiesSection.settings}
              presets={palettePresets}
              api={api}
              anchorRef={tabsRef}
              onChange={(settings) => updateSettings(propertiesSection.id, settings)}
              onClose={() => setPropertiesId(null)}
              showColumns={propertiesSection.type !== "timeline"}
            />
          )}
        </>
      )}

      {pickingType && (
        <SectionTypeDialog onPick={addSection} onCancel={() => setPickingType(false)} />
      )}

      {editing && editingSection && (
        editingSection.type === "timeline" ? (
          <TimelineEditorDialog
            key={editingSection.id}
            section={editingSection}
            api={api}
            activeModules={status?.active_modules ?? []}
            palette={palettePresets.map((preset) => preset.value)}
            presets={palettePresets}
            eventsEnabled={status?.active_modules?.includes("events") ?? false}
            bookingEnabled={status?.active_modules?.includes("booking") ?? false}
            menuEnabled={status?.active_modules?.includes("restaurant_menu") ?? false}
            onSave={(timeline) => saveTimeline(editing.id, timeline)}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <ColumnEditorDialog
            key={`${editingSection.id}-${editing.column}`}
            section={editingSection}
            columnIndex={editing.column}
            api={api}
            designTokens={designTokens}
            activeModules={status?.active_modules ?? []}
            palette={palettePresets.map((preset) => preset.value)}
            onSave={(blocks) => saveColumn(editing.id, editing.column, blocks)}
            onCancel={() => setEditing(null)}
            eventsEnabled={status?.active_modules?.includes("events") ?? false}
            bookingEnabled={status?.active_modules?.includes("booking") ?? false}
            menuEnabled={status?.active_modules?.includes("restaurant_menu") ?? false}
          />
        )
      )}
    </div>
  );
}
